import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { api } from '../App';
import { toast } from 'sonner';
import {
  Sparkles, Copy, ExternalLink, DollarSign,
  MousePointerClick, UserCheck, Clock, CheckCircle,
  Wallet, ArrowDownToLine, Share2
} from 'lucide-react';

const SITE_URL = 'https://www.delfsuccessacademy.com';

const PartnerDashboard = () => {
  const [searchParams] = useSearchParams();
  const codeFromUrl = searchParams.get('code');
  const [partnerCode, setPartnerCode] = useState(codeFromUrl || '');
  const [partner, setPartner] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginCode, setLoginCode] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const referralLink = `${SITE_URL}/?ref=${partner?.partner_code || partnerCode}`;

  useEffect(() => {
    if (codeFromUrl) {
      fetchDashboard(codeFromUrl);
    } else {
      setLoading(false);
    }
  }, [codeFromUrl]);

  const fetchDashboard = async (code) => {
    setLoading(true);
    try {
      const res = await api.get(`/partners/dashboard/${code}`);
      setPartner(res.data.partner);
      setStats(res.data.stats);
      setPartnerCode(code);
    } catch (err) {
      toast.error('Partner not found. Check your code.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginCode) fetchDashboard(loginCode);
    else if (loginEmail) {
      api.get(`/partners/lookup?email=${encodeURIComponent(loginEmail)}`)
        .then(res => fetchDashboard(res.data.partner_code))
        .catch(() => toast.error('Partner not found.'));
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied!');
  };

  const shareOn = (platform) => {
    const text = encodeURIComponent('Prepare for your DELF exam with the best platform. Use my link:');
    const url = encodeURIComponent(referralLink);
    const links = {
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      email: `mailto:?subject=${encodeURIComponent('DELF Exam Preparation')}&body=${text}%20${url}`
    };
    window.open(links[platform], '_blank');
  };

  const handleWithdraw = async () => {
    if (!stats || stats.available_commission < 50) {
      toast.error('Minimum withdrawal is $50.');
      return;
    }
    setWithdrawing(true);
    try {
      await api.post('/partners/withdraw', { partner_code: partnerCode });
      toast.success('Withdrawal request submitted! We will process it shortly.');
      fetchDashboard(partnerCode);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Withdrawal failed.');
    } finally {
      setWithdrawing(false);
    }
  };

  // Login screen if no partner data
  if (!loading && !partner) {
    return (
      <div className="min-h-screen bg-[#FDFCF8]">
        <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-border/50">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>DELF Success Academy</span>
            </Link>
          </div>
        </header>
        <main className="max-w-md mx-auto px-6 py-20">
          <div className="bg-white rounded-2xl p-8 border border-border/50 shadow-lg">
            <h2 className="text-xl font-bold mb-6 text-center" style={{ fontFamily: 'Syne, sans-serif' }}>Partner Dashboard Login</h2>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Partner Code</label>
                <input
                  type="text"
                  value={loginCode}
                  onChange={(e) => setLoginCode(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="e.g. PARTNER123"
                  data-testid="partner-login-code"
                />
              </div>
              <p className="text-xs text-center text-muted-foreground">or</p>
              <div>
                <label className="block text-sm font-medium mb-1">Email Address</label>
                <input
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="your@email.com"
                  data-testid="partner-login-email"
                />
              </div>
              <Button type="submit" className="w-full rounded-full py-5 font-bold" data-testid="partner-login-btn">
                Access Dashboard
              </Button>
              <div className="text-center pt-2">
                <Link to="/partners" className="text-sm text-primary hover:underline">Don&rsquo;t have an account? Become a Partner</Link>
              </div>
            </form>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>DELF Success Academy</span>
          </Link>
          <span className="text-sm font-medium text-muted-foreground">Welcome, {partner.full_name}</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {/* Page Title */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
            Partner Dashboard
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Your partner code: <span className="font-mono font-bold text-primary">{partner.partner_code}</span></p>
        </motion.div>

        {/* Referral Link */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white rounded-2xl p-6 border border-border/50 mb-8" data-testid="referral-link-section">
          <h3 className="font-bold mb-3">My Referral Link</h3>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-[#FDFCF8] border border-border rounded-xl px-4 py-3 text-sm font-mono truncate" data-testid="referral-link-display">
              {referralLink}
            </div>
            <Button onClick={copyLink} className="rounded-xl shrink-0" data-testid="copy-link-btn">
              <Copy className="w-4 h-4 mr-1.5" /> Copy
            </Button>
          </div>
          {/* Share Buttons */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <span className="text-xs font-medium text-muted-foreground mr-1">Share via:</span>
            {[
              { name: 'WhatsApp', key: 'whatsapp', bg: 'bg-[#25D366] hover:bg-[#1fb855] text-white' },
              { name: 'Facebook', key: 'facebook', bg: 'bg-[#1877F2] hover:bg-[#166FE5] text-white' },
              { name: 'X', key: 'twitter', bg: 'bg-gray-900 hover:bg-gray-800 text-white' },
              { name: 'LinkedIn', key: 'linkedin', bg: 'bg-[#0A66C2] hover:bg-[#095baf] text-white' },
              { name: 'Email', key: 'email', bg: 'bg-gray-500 hover:bg-gray-600 text-white' }
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => shareOn(s.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${s.bg}`}
                data-testid={`share-${s.key}-btn`}
              >
                {s.name}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Stats Grid */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="partner-stats-grid">
          {[
            { icon: MousePointerClick, label: 'Referral Clicks', value: stats.clicks, color: 'text-blue-600', bg: 'bg-blue-50' },
            { icon: UserCheck, label: 'Successful Enrollments', value: stats.enrollments, color: 'text-green-600', bg: 'bg-green-50' },
            { icon: Clock, label: 'Pending Commission', value: `$${stats.pending_commission.toFixed(2)}`, color: 'text-amber-600', bg: 'bg-amber-50' },
            { icon: CheckCircle, label: 'Available Commission', value: `$${stats.available_commission.toFixed(2)}`, color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { icon: DollarSign, label: 'Total Earned', value: `$${stats.total_earned.toFixed(2)}`, color: 'text-primary', bg: 'bg-primary/10' },
            { icon: Wallet, label: 'Commission Paid', value: `$${stats.commission_paid.toFixed(2)}`, color: 'text-gray-600', bg: 'bg-gray-50' }
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl p-5 border border-border/50">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center`}>
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                </div>
                <span className="text-xs font-medium text-muted-foreground">{s.label}</span>
              </div>
              <p className="text-2xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
                {s.value}
              </p>
            </div>
          ))}
        </motion.div>

        {/* Withdraw */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white rounded-2xl p-6 border border-border/50 mb-8" data-testid="withdraw-section">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-bold">Withdraw My Earnings</h3>
              <p className="text-sm text-muted-foreground">Minimum withdrawal: $50. Payouts sent to your PayPal.</p>
            </div>
            <Button
              onClick={handleWithdraw}
              disabled={withdrawing || (stats?.available_commission || 0) < 50}
              className="rounded-full px-6 font-semibold"
              data-testid="withdraw-btn"
            >
              <ArrowDownToLine className="w-4 h-4 mr-1.5" />
              {withdrawing ? 'Processing...' : 'Withdraw'}
            </Button>
          </div>
        </motion.div>

        {/* Commission Structure */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl p-6 border border-border/50">
          <h3 className="font-bold mb-4">Commission Structure (20%)</h3>
          <div className="grid sm:grid-cols-4 gap-3">
            {[
              { level: 'A2', earn: '$29.40', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
              { level: 'B1', earn: '$39.40', color: 'bg-blue-100 text-blue-800 border-blue-300' },
              { level: 'B2', earn: '$49.40', color: 'bg-purple-100 text-purple-800 border-purple-300' },
              { level: 'Coaching', earn: '$99.40', color: 'bg-amber-100 text-amber-800 border-amber-300' }
            ].map((c) => (
              <div key={c.level} className={`rounded-xl border p-4 text-center ${c.color}`}>
                <p className="font-bold">{c.level}</p>
                <p className="text-lg font-bold">{c.earn}</p>
                <p className="text-xs opacity-70">per sale</p>
              </div>
            ))}
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border bg-white mt-10">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-muted-foreground">&copy; 2026 DELF Success Academy. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PartnerDashboard;
